import React, { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';

/**
 * WhyChooseUs section component for the homepage
 * Highlights the benefits of translating games into French
 * Designed with an elegant, minimalistic black and white approach
 * Supports multiple languages through i18n
 */
const WhyChooseUs = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  
  const reasons = [
    {
      number: t('whyChooseUs.reason1.number'),
      title: t('whyChooseUs.reason1.title'),
      description: t('whyChooseUs.reason1.description')
    },
    {
      number: t('whyChooseUs.reason2.number'),
      title: t('whyChooseUs.reason2.title'),
      description: t('whyChooseUs.reason2.description')
    },
    {
      number: t('whyChooseUs.reason3.number'),
      title: t('whyChooseUs.reason3.title'),
      description: t('whyChooseUs.reason3.description')
    }
  ];

  // GSAP animations
  useEffect(() => {
    if (isInView) {
      const ctx = gsap.context(() => {
        // Animate the title elements
        gsap.from(titleRef.current.querySelectorAll('div, h2, p'), {
          y: 30,
          opacity: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power3.out"
        });
      }, sectionRef);
      
      return () => ctx.revert();
    }
  }, [isInView]);

  // Animation variants for cards
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  return (
    <section ref={sectionRef} className="py-32 bg-bg-alt scroll-section relative overflow-hidden">
      {/* Banner image as decorative element - centered */}
      <div className="absolute inset-0 w-full h-full opacity-10 z-0 flex justify-center items-center">
        <img 
          src="/natux-banner.jpg" 
          alt="" 
          className="w-full h-full object-cover object-center"
          style={{ filter: 'grayscale(100%)' }}
        />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div ref={titleRef} className="max-w-3xl mx-auto mb-24 text-center">
          <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
          <h2 className="font-heading text-3xl md:text-5xl font-normal mb-8 tracking-wide">
            {t('whyChooseUs.title')}
          </h2>
          <p className="text-light text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            {t('whyChooseUs.subtitle')}
          </p>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {reasons.map((reason, index) => (
            <motion.div 
              key={index} 
              className="border-t border-primary/20 pt-10 reveal"
              variants={itemVariants}
            >
              <span className="text-primary font-light text-sm tracking-widest mb-6 block">{reason.number}</span>
              <h3 className="text-2xl font-heading mb-6 tracking-wide">{reason.title}</h3>
              <p className="text-light font-light text-lg">{reason.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      {/* Decorative element */}
      <div className="absolute -bottom-10 right-0 w-1/3 h-px bg-primary/10"></div>
    </section>
  );
};

export default WhyChooseUs;
